/**
 * TABBED CONTENT SECTION - JavaScript
 */

if (!customElements.get('tabbed-content-section')) {
  class TabbedContentSection extends HTMLElement {
    constructor() {
      super();
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.activeTabIndex = 0;
      this.isInitialized = false;
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        requestAnimationFrame(() => this.init());
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      if (this.isInitialized) return;
      
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      
      if (!this.tabs.length || !this.panels.length) {
        console.warn('TabbedContentSection: No tabs or panels found');
        return;
      }

      console.log('Tabs found:', this.tabs.length);
      console.log('Panels found:', this.panels.length);
      
      this.sectionId = this.dataset.sectionId;
      this.defaultTabIndex = parseInt(this.dataset.defaultTab, 10) || 0;
      
      // Activate default tab
      this.activateTab(this.defaultTabIndex);
      
      // Attach event listeners
      this.tabs.forEach(tab => {
        tab.addEventListener('click', this.handleTabClick);
      });
      
      if (this.tablist) {
        this.tablist.addEventListener('keydown', this.handleKeydown);
      }
      
      this.attachEditorListeners();
      this.isInitialized = true;
    }

    destroy() {
      if (!this.isInitialized) return;
      
      this.tabs.forEach(tab => {
        tab.removeEventListener('click', this.handleTabClick);
      });
      
      if (this.tablist) {
        this.tablist.removeEventListener('keydown', this.handleKeydown);
      }
      
      this.isInitialized = false;
    }

    attachEditorListeners() {
      if (typeof Shopify === 'undefined' || !Shopify.designMode) return;
      
      document.addEventListener('shopify:section:load', (event) => {
        if (event.detail.sectionId === this.sectionId) {
          this.destroy();
          requestAnimationFrame(() => this.init());
        }
      });
      
      document.addEventListener('shopify:block:select', (event) => {
        if (event.detail.sectionId === this.sectionId) {
          const blockId = event.detail.blockId;
          const tabIndex = this.tabs.findIndex(tab => tab.dataset.blockId === blockId);
          if (tabIndex !== -1) {
            this.activateTab(tabIndex);
          }
        }
      });
    }

    handleTabClick(event) {
      event.preventDefault();
      const tab = event.currentTarget;
      const tabIndex = parseInt(tab.dataset.tabIndex, 10);
      
      console.log('Tab clicked, index:', tabIndex);
      
      if (!isNaN(tabIndex)) {
        this.activateTab(tabIndex);
      }
    }

    handleKeydown(event) {
      const { key } = event;

      // Find current position in the DOM tabs array
      const currentArrayPos = this.tabs.findIndex(
        t => parseInt(t.dataset.tabIndex, 10) === this.activeTabIndex
      );
      let newArrayPos = currentArrayPos;

      switch (key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          newArrayPos = currentArrayPos - 1;
          if (newArrayPos < 0) newArrayPos = this.tabs.length - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          newArrayPos = currentArrayPos + 1;
          if (newArrayPos >= this.tabs.length) newArrayPos = 0;
          break;
        case 'Home':
          event.preventDefault();
          newArrayPos = 0;
          break;
        case 'End':
          event.preventDefault();
          newArrayPos = this.tabs.length - 1;
          break;
        default:
          return;
      }

      if (newArrayPos !== currentArrayPos) {
        const newTabIndex = parseInt(this.tabs[newArrayPos].dataset.tabIndex, 10);
        this.activateTab(newTabIndex);
        this.tabs[newArrayPos].focus();
      }
    }

    activateTab(index) {
      // Find tab by data-tab-index attribute (not array position)
      const targetTab = this.tabs.find(t => parseInt(t.dataset.tabIndex, 10) === index);

      if (!targetTab) {
        console.warn('No tab found with data-tab-index:', index);
        return;
      }

      // Update all tabs - match by reference
      this.tabs.forEach((tab) => {
        const isActive = tab === targetTab;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.classList.toggle('is-active', isActive);
      });

      // Update all panels - match by data-panel-index
      this.panels.forEach((panel) => {
        const panelIndex = parseInt(panel.dataset.panelIndex, 10);
        const isActive = panelIndex === index;

        panel.classList.toggle('is-active', isActive);

        if (isActive) {
          panel.removeAttribute('hidden');
          panel.style.display = 'block';
        } else {
          panel.setAttribute('hidden', '');
          panel.style.display = 'none';
        }
      });

      this.activeTabIndex = index;
    }
  }

  customElements.define('tabbed-content-section', TabbedContentSection);
}